import { PhylloUserAnalyticsData } from '@round/api';
import { Card, ChartConfig, ChartContainer, recharts, Empty, lucideIcons, Spinner } from '@round/creatorbase-ui';
import { useMemo } from 'react';
import uniq from 'lodash/uniq';
import { Gender, GENDER_COLORS } from './helpers';
import { toDecimalPoint } from '@round/utils';
import styles from './Audience.module.css';

const { SearchXIcon, CircleAlertIcon } = lucideIcons;
const { BarChart, Bar, XAxis, LabelList } = recharts;
const BAR_RADIUS: [number, number, number, number] = [3, 3, 0, 0];

type DistributionData = { age: string } & Record<Gender, number>;

type Props = {
    data: PhylloUserAnalyticsData['profile']['audience']['gender_age_distribution'];
    isLoading?: boolean;
    errorMessage: string | null;
};

const AgeGenderDistributionChart = ({ data, isLoading, errorMessage }: Props) => {
    const isError = !!errorMessage;

    const distributionData: DistributionData[] = useMemo(() => {
        const ageRanges = uniq(data.map((item) => item.age_range));
        return ageRanges
            .map((age) => ({ age, male: 0, female: 0, others: 0 }))
            .map(({ age }) => {
                return {
                    age,
                    male: data.find((item) => item.age_range === age && item.gender === 'MALE')?.value ?? 0,
                    female: data.find((item) => item.age_range === age && item.gender === 'FEMALE')?.value ?? 0,
                    others: data.find((item) => item.age_range === age && item.gender === 'OTHERS')?.value ?? 0,
                };
            });
    }, [data]);

    const counts = useMemo(() => {
        return data.reduce(
            (acc, item) => {
                acc[item.gender.toLowerCase() as Gender] = acc[item.gender.toLowerCase() as Gender] ?? 0 + item.value;
                return acc;
            },
            {} as Record<Gender, number>
        );
    }, [data]);

    const chartConfig: ChartConfig = useMemo(() => {
        return {
            male: {
                label: 'Male',
                color: GENDER_COLORS.male,
            },
            female: {
                label: 'Female',
                color: GENDER_COLORS.female,
            },
            others: {
                label: 'Others',
                color: GENDER_COLORS.others,
            },
        };
    }, []);

    const toPercentage = (value: number) => {
        return `${toDecimalPoint(value, 2)}%`;
    };

    return (
        <Card size="sm" variant="borderless" className={styles.chartContainer}>
            <Card.Header className={styles.chartHeader}>
                <Card.Title>Age</Card.Title>
            </Card.Header>

            <Card.Content className={styles.chartContent}>
                {isLoading && (
                    <div className={styles.spinnerContainer}>
                        <Spinner className={styles.spinner} />
                    </div>
                )}

                {!isLoading && distributionData.length === 0 && (
                    <Empty>
                        <Empty.Media variant="icon">{isError ? <CircleAlertIcon /> : <SearchXIcon />}</Empty.Media>
                        <Empty.Title>{isError ? 'Something went wrong' : 'No data available'}</Empty.Title>
                        <Empty.Description>
                            {isError
                                ? errorMessage ?? 'Failed to load age gender distribution. Please try again.'
                                : 'Please try again later.'}
                        </Empty.Description>
                    </Empty>
                )}

                {!isLoading && distributionData.length > 0 && (
                    <ChartContainer config={chartConfig} className={styles.chart}>
                        <BarChart accessibilityLayer data={distributionData}>
                            {!!counts.male && (
                                <Bar dataKey="male" fill={GENDER_COLORS.male} radius={BAR_RADIUS}>
                                    <LabelList
                                        position="top"
                                        offset={4}
                                        fontSize={10}
                                        color="var(--foreground)"
                                        formatter={(value) => toPercentage(Number(value))}
                                    />
                                </Bar>
                            )}

                            {!!counts.female && (
                                <Bar dataKey="female" fill={GENDER_COLORS.female} radius={BAR_RADIUS}>
                                    <LabelList
                                        position="top"
                                        offset={4}
                                        fontSize={10}
                                        color="var(--foreground)"
                                        formatter={(value) => toPercentage(Number(value))}
                                    />
                                </Bar>
                            )}

                            {!!counts.others && (
                                <Bar dataKey="others" fill={GENDER_COLORS.others} radius={BAR_RADIUS}>
                                    <LabelList
                                        position="top"
                                        offset={4}
                                        fontSize={10}
                                        color="var(--foreground)"
                                        formatter={(value) => toPercentage(Number(value))}
                                    />
                                </Bar>
                            )}

                            <XAxis dataKey="age" />
                            <ChartContainer.Tooltip
                                content={
                                    <ChartContainer.TooltipContent
                                        formatter={(value, name) => {
                                            const config = chartConfig[name as Gender];
                                            const label = config?.label;
                                            const percentage = toPercentage(Number(value));
                                            return (
                                                <span style={{ color: config?.color, fontSize: '12px' }}>
                                                    {label}: {percentage}
                                                </span>
                                            );
                                        }}
                                    />
                                }
                            />
                        </BarChart>
                    </ChartContainer>
                )}
            </Card.Content>
        </Card>
    );
};

export default AgeGenderDistributionChart;
